import { animate, style, transition, trigger } from '@angular/animations';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { AsyncPipe, JsonPipe } from '@angular/common';
import {
  Component,
  computed,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { Database, listVal, objectVal, ref, set } from '@angular/fire/database';
import { Firestore, Timestamp } from '@angular/fire/firestore';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import { Observable, of, Subject, takeUntil } from 'rxjs';
import { AppExceptionModel } from '../../../models/app-exception.model';
import { ProtestantCanonBooks } from '../../../models/books.bible.model';
import { Member } from '../../../models/member.model';
import { QuizQuestion } from '../../../models/question.quiz.model';
import { Quiz, QuizStatus } from '../../../models/quiz.model';
import { UserModel } from '../../../models/user.model';
import { QuizService } from '../../../services/quiz.service';
import { UtilsService } from '../../../services/utils.service';
import { AppState } from '../../../state/appstate';
import { BtnActionComponent } from '../../components/buttons/btn-action/btn-action.component';
import { BtnBackComponent } from '../../components/buttons/btn-back/btn-back.component';
import { BtnFabComponent } from '../../components/buttons/btn-fab/btn-fab.component';
import { QuestionSearchComponent } from '../question-search/question-search.component';
import {
  ChipColor,
  ChipComponent,
} from '../../components/inputs/chip/chip.component';

@Component({
  selector: 'app-create-quiz',
  standalone: true,
  imports: [
    BtnBackComponent,
    BtnActionComponent,
    ChipComponent,
    ReactiveFormsModule,
    AsyncPipe,
    JsonPipe,
    CdkDrag,
    CdkDropList,
    BtnFabComponent,
    QuestionSearchComponent,
  ],
  templateUrl: './create-quiz.component.html',
  animations: [
    // Animation for the Backdrop (Fade)
    trigger('fade', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms ease-out', style({ opacity: 1 })),
      ]),
      transition(':leave', [animate('200ms ease-in', style({ opacity: 0 }))]),
    ]),
    // Animation for the Panel (Slide)
    trigger('slideInOut', [
      transition(
        ':enter',
        [
          // Start state: if mobile, below screen; if desktop, off-right
          style({ transform: '{{startPos}}' }),
          animate(
            '300ms cubic-bezier(0.25, 0.8, 0.25, 1)',
            style({ transform: 'translate(0,0)' }),
          ),
        ],
        { params: { startPos: 'translateX(100%)' } },
      ), // Default param
      transition(
        ':leave',
        [animate('250ms ease-in', style({ transform: '{{startPos}}' }))],
        { params: { startPos: 'translateX(100%)' } },
      ),
    ]),
  ],
  styleUrl: './create-quiz.component.css',
})
export class CreateQuizComponent implements OnInit, OnDestroy {
  showSearch = false;
  isMobile = false;

  get animationParams() {
    return {
      value: 'visible',
      params: {
        startPos: this.isMobile ? 'translateY(100%)' : 'translateX(100%)',
      },
    };
  }

  store: Store<AppState> = inject(Store<AppState>);
  router: Router = inject(Router);
  private db: Database = inject(Database);
  private firestore: Firestore = inject(Firestore);
  fb: FormBuilder = inject(FormBuilder);
  activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private logger: NGXLogger = inject(NGXLogger);
  private quizService = inject(QuizService);
  private utilsService = inject(UtilsService);

  groupId: string | null = this.activatedRoute.snapshot.paramMap.get('id')!;
  quizId: string | null = null;

  user: UserModel | null = null;
  quiz: Quiz = {} as Quiz;
  students$: Observable<Member[]> = of([]);
  session$: Observable<any> = of(null);

  selectedQuestions = signal<QuizQuestion[]>([]);
  addQuestions: string[] = [];
  searchedQuestions$: Observable<QuizQuestion[]> = of([]);

  isLoading = signal(false);
  isWorking = signal(false);
  currentStep = signal(0);
  quizForm: FormGroup;

  chipColor = ChipColor;
  protestantBooks = ProtestantCanonBooks;

  totalScore = computed(() => {
    return this.selectedQuestions().reduce((acc, q) => acc + (q.score || 0), 0);
  });

  exit$ = new Subject<void>();

  constructor() {
    this.quizForm = this.fb.group({
      title: [null, [Validators.required]],
      answerDuration: [
        30,
        [Validators.required, Validators.min(0), Validators.max(60)],
      ],
      groupId: [this.groupId, [Validators.required]],
      createdById: [, [Validators.required]],
      createdByName: [, [Validators.required]],
    });
    this.quiz.answerDuration = 30;
  }

  onSearchResults(questions: QuizQuestion[]) {
    this.searchedQuestions$ = of(questions);
  }

  onSearchCleared() {
    this.searchedQuestions$ = of([]);
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  ngOnInit(): void {
    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.exit$))
      .subscribe((user) => {
        this.user = user;
        this.quizForm.patchValue({
          title: `${this.user?.name}'s Quiz - ${new Date().toLocaleDateString()}`,
          answerDuration: this.quiz.answerDuration,
          createdById: this.user?.uid,
          createdByName: this.user?.name,
        });
      });
  }

  @HostListener('window:resize')
  checkScreenSize() {
    this.isMobile = window.innerWidth < 768;
  }

  toggleSearch() {
    this.showSearch = !this.showSearch;
  }

  setDuration(event: Event) {
    this.quiz.answerDuration = event.target
      ? +(event.target as HTMLInputElement).value
      : 30;
  }

  setTitle(event: Event) {
    this.quizForm.patchValue({
      title: event.target ? (event.target as HTMLInputElement).value : '',
    });
  }

  toggleQuestion(q: any, addOrRemoveFlag: boolean) {
    if (addOrRemoveFlag) {
      this.addQuestion(q);
    } else {
      this.removeQuestion(q);
    }
  }

  addQuestion(q: any) {
    if (!this.selectedQuestions().find((item) => item.id === q.id)) {
      this.selectedQuestions.update((questions) => [...questions, q]);
    }
  }

  removeQuestion(q: any) {
    this.selectedQuestions.update((questions) =>
      questions.filter((item) => item.id !== q.id),
    );
  }

  isQuestionSelected(questionId: string): boolean {
    return this.selectedQuestions().some((q) => q.id === questionId);
  }



  changeStep(direction: number) {
    this.currentStep.set(this.currentStep() + direction);
  }

  createRoom() {
    if (this.quizForm.invalid) {
      this.logger.error(
        'Missing required information to create quiz session. Group ID: ',
        this.groupId,
        ' User ID: ',
        this.user?.uid,
        ' User Name: ',
        this.user?.name,
      );
      this.utilsService.onShowAlert({
        code: 'ERR_USER_INFO_MISSING',
        title: 'Error Creating Quiz',
        message: 'Missing required information. Please try again later.',
      } as AppExceptionModel);
      return;
    }
    this.isWorking.set(true);
    this.quiz.inviteCode = UtilsService.createInviteCode();
    this.quiz.questions = this.selectedQuestions().map((q) => q.id!);
    this.quiz.title = this.quizForm.value.title;
    this.quiz.groupId = this.quizForm.value.groupId;
    this.quiz.quizStatus = QuizStatus.waiting;
    this.quiz.totalScore = this.totalScore();
    this.quiz.scheduledAt = Timestamp.now();
    this.quiz.isLive = true;
    this.quiz.createdById = this.quizForm.value.createdById;
    this.quiz.createdByName = this.quizForm.value.createdByName;
    this.quizService
      .createQuiz(this.quiz)
      .then((id) => {
        this.quizId = id;

        // Initialize the RTDB Session so students don't get Permission Denied
        const sessionRef = ref(
          this.db,
          `sessions/${this.quizForm.value.groupId}/${this.quizId}`,
        );
        return set(sessionRef, {
          status: QuizStatus.waiting,
          currentIndex: -1, // -1 means they are in the lobby
          inviteCode: this.quiz.inviteCode,
          startTime: Date.now(),
        });
      })
      .then(() => {
        this.logger.debug('Quiz session initialized in Realtime Database');
        this.router.navigate(['..', this.quizId, 'live'], {
          relativeTo: this.activatedRoute,
        });
      })
      .catch((error) => {
        this.logger.error('Error creating quiz session: ', error);
        this.utilsService.onShowAlert(
          {
            code: 'CREATE_QUIZ_ERROR',
            title: 'Error Creating Quiz',
            message: 'Failed to create quiz. Please try again later.',
          } as AppExceptionModel,
          false,
        );
      })
      .finally(() => {
        this.isWorking.set(false);
      });
  }

  streamStudentsList() {
    if (!this.groupId || !this.quizId) return;

    const presencePath = ref(
      this.db,
      `presence/${this.quizForm.value.groupId}/${this.quizId}`,
    );
    const sessionPath = ref(
      this.db,
      `sessions/${this.quizForm.value.groupId}/${this.quizId}`,
    );
    this.students$ = listVal(presencePath);

    // Track the session status
    this.session$ = objectVal(sessionPath);
  }

  drop(event: CdkDragDrop<QuizQuestion[]>) {
    moveItemInArray(
      event.container.data,
      event.previousIndex,
      event.currentIndex,
    );
    this.selectedQuestions.set([...this.selectedQuestions()]);
  }

  copyInviteCode() {
    navigator.clipboard.writeText(this.quiz.inviteCode!);
  }
}
